import { useState } from 'react';
import { NEWS_ITEMS, ASSET_CATEGORIES } from '@/data/mockData';
import { useAppContext } from '@/context/AppContext';
import { useLiveNews } from '@/hooks/useLiveNews';
import { useMarketData } from '@/hooks/useMarketData';
import { SentimentBadge } from '@/components/SentimentBadge';
import {
  Bookmark, BookmarkCheck, ChevronDown, ChevronUp,
  Sparkles, Clock, ExternalLink, Zap, RefreshCw, Loader2, TrendingUp,
} from 'lucide-react';
import type { AssetCategory, NewsItem } from '@/types';
import type { MarketQuote } from '@/hooks/useMarketData';

function NewsCard({ item, isExpanded, isBookmarked, onToggleExpand, onToggleBookmark }: {
  item: NewsItem;
  isExpanded: boolean;
  isBookmarked: boolean;
  onToggleExpand: () => void;
  onToggleBookmark: () => void;
}) {
  return (
    <div
      className={`bg-card rounded-xl border transition-all duration-300 ${
        isExpanded ? 'shadow-md border-primary/20 ring-1 ring-primary/10' : 'shadow-sm border-border/50 hover:border-border'
      }`}
      data-testid={`news-card-${item.id}`}
    >
      <div className="p-5 cursor-pointer flex flex-col gap-4" onClick={onToggleExpand}>
        <div className="flex items-start justify-between gap-4">
          <h2 className={`font-bold text-lg leading-snug transition-colors ${isExpanded ? 'text-primary' : 'text-foreground hover:text-primary/80'}`}>
            {item.title}
          </h2>
          <button
            onClick={e => { e.stopPropagation(); onToggleBookmark(); }}
            className="p-2 -mr-2 -mt-2 rounded-full hover:bg-secondary text-muted-foreground transition-colors"
            data-testid={`btn-bookmark-news-${item.id}`}
            aria-label="收藏"
          >
            {isBookmarked
              ? <BookmarkCheck size={22} className="text-accent fill-accent" />
              : <Bookmark size={22} />}
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div className="space-y-3 flex-1">
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground font-medium">
              <span className="bg-secondary px-2 py-0.5 rounded text-foreground/80">{item.category}</span>
              <span className="flex items-center gap-1"><Clock size={12} /> {new Date(item.publishedAt).toLocaleDateString('zh-CN')}</span>
              <span>{item.source}</span>
              <SentimentBadge sentiment={item.sentiment} />
            </div>
            {!isExpanded && (
              <div className="bg-primary/[0.03] border border-primary/10 rounded-lg p-3 flex gap-3 text-sm text-foreground/80 leading-relaxed">
                <Sparkles size={16} className="text-accent flex-shrink-0 mt-0.5" />
                <span className="line-clamp-2">{item.summary}</span>
              </div>
            )}
          </div>
          <div className="flex justify-center sm:justify-end text-muted-foreground p-1">
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-5 pb-5 pt-2 border-t border-border/40 animate-in slide-in-from-top-2 duration-200">
          <div className="bg-primary/5 border border-primary/15 rounded-lg p-4 mb-6 relative">
            <div className="absolute top-0 left-4 -mt-2.5 bg-card px-2 text-xs font-bold text-primary flex items-center gap-1 rounded-sm border border-primary/10">
              <Sparkles size={12} />AI 深度摘要
            </div>
            <p className="text-sm font-medium leading-relaxed text-foreground/90 mt-2">{item.summary}</p>
          </div>
          <div className="space-y-4 text-foreground/80 text-sm md:text-base leading-loose">
            <p>{item.content}</p>
          </div>
          <div className="mt-6 flex justify-end">
            {item.url ? (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                onClick={e => e.stopPropagation()}
              >
                查看原文 <ExternalLink size={14} />
              </a>
            ) : (
              <span
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"
                title="当前数据源没有返回对应的原文地址"
              >
                原文链接暂不可用
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StockIndexCard({ name, quote }: { name: string; quote: MarketQuote }) {
  const isUp = quote.change > 0;
  const isDown = quote.change < 0;
  const colorClass = isUp
    ? 'text-rose-600 dark:text-rose-400'
    : isDown
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-foreground';
  const sign = isUp ? '+' : '';

  return (
    <div className="min-w-[132px] flex-1 px-4 py-4" data-testid={`stock-index-${name}`}>
      <div className={`text-xl md:text-2xl font-bold tabular-nums tracking-tight ${colorClass}`}>
        {quote.price.toLocaleString('zh-CN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-sm font-semibold text-foreground">{name}</span>
        <span className={`text-sm font-semibold tabular-nums ${colorClass}`}>
          {sign}{quote.changePct}%
        </span>
      </div>
    </div>
  );
}

function StockMarketSnapshot({
  data,
  loading,
  error,
}: {
  data: ReturnType<typeof useMarketData>['data'];
  loading: boolean;
  error: string | null;
}) {
  const quotes = [
    { name: '上证', quote: data?.sh },
    { name: '深成', quote: data?.sz },
    { name: '创业板', quote: data?.cyb },
  ].filter((item): item is { name: string; quote: MarketQuote } => Boolean(item.quote));

  if (loading && quotes.length === 0) {
    return (
      <section className="rounded-xl border border-border/50 bg-card p-4 shadow-sm" aria-label="A股主要指数加载中">
        <div className="mb-3 h-4 w-28 animate-pulse rounded bg-muted" />
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map(item => (
            <div key={item} className="h-16 animate-pulse rounded-lg bg-muted/60" />
          ))}
        </div>
      </section>
    );
  }

  if (quotes.length === 0) {
    return (
      <section className="rounded-xl border border-border/50 bg-card px-4 py-4 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <TrendingUp size={16} className="text-accent" />
          A股主要指数
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          行情源暂未返回数据，实时快讯仍会继续更新。
        </p>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm" aria-label="A股主要指数">
      <div className="flex items-center justify-between border-b border-border/40 bg-primary/[0.025] px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <TrendingUp size={16} className="text-accent" />
          A股主要指数
          <span className={`text-xs font-normal ${error ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-500'}`}>
            {error ? '● 最近可用' : '● 实时'}
          </span>
        </div>
        {data?.updatedAt && (
          <span className="text-xs text-muted-foreground tabular-nums">
            {new Date(data.updatedAt).toLocaleTimeString('zh-CN', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>
      <div className="flex divide-x divide-border/40 overflow-x-auto">
        {quotes.map(item => (
          <StockIndexCard key={item.name} name={item.name} quote={item.quote} />
        ))}
      </div>
      <div className="border-t border-border/30 px-4 py-2 text-[11px] text-muted-foreground">
        {error ? '实时行情暂时不可用，以上为最近一次有效数据。' : '数据来源：新浪财经 · 交易时段每分钟更新'}
      </div>
    </section>
  );
}

export function News() {
  const { bookmarkedNewsIds, toggleBookmarkedNews } = useAppContext();
  const [activeCategory, setActiveCategory] = useState<AssetCategory | '全部'>('全部');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const {
    items: liveItems,
    loading: liveLoading,
    updatedAt,
    isLive,
    error: liveError,
    refetch,
  } = useLiveNews();
  const {
    data: marketData,
    loading: marketLoading,
    refetch: refetchMarket,
    error: marketError,
  } = useMarketData();

  const toggleExpand = (id: string) => setExpandedId(prev => prev === id ? null : id);

  const categoryLiveItems = activeCategory === '全部'
    ? liveItems
    : liveItems.filter(item => item.category === activeCategory);

  const filteredMock = activeCategory === '全部'
    ? NEWS_ITEMS
    : NEWS_ITEMS.filter(item => item.category === activeCategory);

  const hasCategoryLiveData = isLive && categoryLiveItems.length > 0;
  const displayedLiveItems = (
    hasCategoryLiveData ? categoryLiveItems : filteredMock
  ).slice(0, activeCategory === '全部' ? 20 : 12);
  const deepAnalysisItems = (
    hasCategoryLiveData
      ? categoryLiveItems.filter(item => item.url)
      : filteredMock
  ).slice(0, activeCategory === '全部' ? 12 : 6);

  const handleRefresh = () => {
    refetch();
    if (activeCategory === '股市') refetchMarket();
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex-1 w-full max-w-5xl mx-auto py-6 px-4 md:px-8 space-y-6 animate-in fade-in duration-500 pb-24 md:pb-8">
      {/* Header & Filters */}
      <div className="space-y-5 sticky top-0 bg-background/95 backdrop-blur-md pt-2 pb-4 z-20 border-b">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">市场要闻</h1>
        <div className="flex overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 hide-scrollbar gap-2">
          <button
            onClick={() => setActiveCategory('全部')}
            data-testid="filter-news-全部"
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
              activeCategory === '全部'
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
          >
            全部
          </button>
          {ASSET_CATEGORIES.map(category => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              data-testid={`filter-news-${category}`}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                activeCategory === category
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {activeCategory === '股市' && (
        <StockMarketSnapshot data={marketData} loading={marketLoading} error={marketError} />
      )}

      {(liveError || (activeCategory === '股市' && marketError)) && (
        <div
          role="status"
          className="flex items-start justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200"
        >
          <span>{liveError || marketError}</span>
          <button
            type="button"
            onClick={handleRefresh}
            className="flex-shrink-0 font-medium underline underline-offset-2 hover:no-underline"
          >
            重试
          </button>
        </div>
      )}

      {/* ── 实时快讯区 ── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold flex items-center gap-2">
            <Zap size={16} className="text-accent" />
            实时快讯
            {hasCategoryLiveData
              ? <span className="text-xs font-normal text-emerald-500 ml-1">● 实时</span>
              : <span className="text-xs font-normal text-muted-foreground ml-1">精编备用</span>
            }
          </h2>
          <div className="flex items-center gap-2">
            {hasCategoryLiveData && updatedAt && (
              <span className="text-xs text-muted-foreground font-mono hidden sm:block">
                {new Date(updatedAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            <button
              onClick={handleRefresh}
              disabled={liveLoading || (activeCategory === '股市' && marketLoading)}
              className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground transition-colors disabled:opacity-50"
              title="刷新快讯"
            >
              {liveLoading || (activeCategory === '股市' && marketLoading)
                ? <Loader2 size={14} className="animate-spin" />
                : <RefreshCw size={14} />}
            </button>
          </div>
        </div>

        {liveLoading && liveItems.length === 0 ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-muted/50 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : displayedLiveItems.length === 0 ? (
          <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg p-4">
            该分类暂无可用资讯
          </div>
        ) : (
          <div className="bg-card border border-border/50 rounded-xl overflow-hidden shadow-sm divide-y divide-border/40">
            {displayedLiveItems.map(item => (
              <div
                key={item.id}
                className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors group"
                data-testid={`live-news-${item.id}`}
              >
                <SentimentBadge sentiment={item.sentiment} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm leading-snug text-foreground/90 group-hover:text-foreground transition-colors line-clamp-2">
                    {item.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span className="bg-secondary px-1.5 py-0.5 rounded">{item.category}</span>
                    <span>{item.source}</span>
                    <span className="font-mono">{formatTime(item.publishedAt)}</span>
                  </div>
                </div>
                {item.url && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 p-1 rounded text-muted-foreground hover:text-primary transition-colors opacity-0 group-hover:opacity-100"
                    onClick={e => e.stopPropagation()}
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── AI 精编深度分析 ── */}
      <section>
        <h2 className="text-base font-bold flex items-center gap-2 mb-4">
          <Sparkles size={16} className="text-accent" />
          AI 精编·深度分析
        </h2>
        <div className="space-y-4">
          {deepAnalysisItems.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">暂无该分类的分析</div>
          ) : (
            deepAnalysisItems.map(item => (
              <NewsCard
                key={item.id}
                item={item}
                isExpanded={expandedId === item.id}
                isBookmarked={bookmarkedNewsIds.includes(item.id)}
                onToggleExpand={() => toggleExpand(item.id)}
                onToggleBookmark={() => toggleBookmarkedNews(item.id)}
              />
            ))
          )}
        </div>
      </section>
    </div>
  );
}
